import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    TournamentRegistrationsContextProps,
    TournamentRegistrationsContextValues,
} from "../types/contexts";
import { Filters } from "../types/tournamentregistrations";

import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { defaultDateFormat } from "../../../../../lib/utils";
import { IOrder } from "../../../../../types/api";
import { AsyncStatuses } from "../../../../../types/shared";

const TournamentRegistrationsContext =
    createContext<TournamentRegistrationsContextValues | null>(null);

export const useTournamentRegistrationsContext = () => {
    const context = useContext(TournamentRegistrationsContext);
    if (!context) {
        throw new Error(
            "useTournamentRegistrationsContext must be used within a TournamentRegistrationsProvider",
        );
    }
    return context;
};

export const TournamentRegistrationsProvider: React.FC<
    TournamentRegistrationsContextProps
> = props => {
    const { children, initialOrders } = props;

    const [orders, setOrders] = useState<IOrder[]>(initialOrders);
    const [fetchStatus, setFetchStatus] = useState<AsyncStatuses>(undefined);

    const tournaments = useAppSelector(
        state => state.tournamentmanagement.tournaments,
    );
    const masterTournaments = useAppSelector(
        state => state.tournamentmanagement.masterTournaments,
    );

    const defaultStatusFilters: Array<IOrder["status"]> = [
        "completed",
        "pending",
    ];

    const getInitialFilters = (): Filters => {
        const savedFilters = sessionStorage.getItem(
            "tournamentRegistrationsFilters",
        );
        if (savedFilters) {
            try {
                return JSON.parse(savedFilters) as Filters;
            } catch (error) {
                console.error(
                    "Failed to parse saved filters from localStorage.",
                    error,
                );
            }
        }

        return {
            ageLevels: {
                options: Array.from(
                    new Set(
                        orders.flatMap(o =>
                            o.products ? o.products.map(p => p.name) : [],
                        ),
                    ),
                ).sort((a, b) => {
                    return parseInt(a) - parseInt(b);
                }),
                filter: undefined,
            },
            search: "",
            orderCompletionStart: undefined,
            orderCompletionEnd: undefined,
            orderDateEnd: undefined,
            orderDateStart: undefined,
            statuses: {
                options: defaultStatusFilters,
                filter: ["completed", "pending"],
            },
        };
    };

    const [filters, setFilters] = useState<Filters>(getInitialFilters());

    useEffect(() => {
        sessionStorage.setItem(
            "tournamentRegistrationsFilters",
            JSON.stringify(filters),
        );
    }, [filters]);

    const filteredOrders = useMemo(
        () =>
            orders
                .filter(o =>
                    filters.ageLevels.filter
                        ? o.products.some(p =>
                              filters.ageLevels.filter?.includes(p.name),
                          )
                        : true,
                )
                .filter(o =>
                    filters.statuses.filter
                        ? filters.statuses.filter.includes(o.status)
                        : true,
                )
                .filter(o => {
                    return filters.orderDateStart
                        ? new Date(
                              defaultDateFormat.format(new Date(o.date)),
                          ) >= new Date(filters.orderDateStart)
                        : true;
                })
                .filter(o => {
                    return filters.orderDateEnd
                        ? new Date(
                              defaultDateFormat.format(new Date(o.date)),
                          ) <= new Date(filters.orderDateEnd)
                        : true;
                })
                .filter(o => {
                    return filters.orderCompletionStart
                        ? o.completionDate
                            ? new Date(
                                  defaultDateFormat.format(
                                      new Date(o.completionDate),
                                  ),
                              ) >= new Date(filters.orderCompletionStart)
                            : false
                        : true;
                })
                .filter(o => {
                    return filters.orderCompletionEnd
                        ? o.completionDate
                            ? new Date(
                                  defaultDateFormat.format(
                                      new Date(o.completionDate),
                                  ),
                              ) <= new Date(filters.orderCompletionEnd)
                            : false
                        : true;
                })
                .filter(o =>
                    filters.search
                        ? o.products.some(
                              p =>
                                  (p.meta.teamName
                                      ? p.meta.teamName
                                            .toLowerCase()
                                            .includes(
                                                filters.search!.toLowerCase(),
                                            )
                                      : false) ||
                                  p.description
                                      .toLowerCase()
                                      .includes(filters.search!.toLowerCase()),
                          ) ||
                          o.user.email
                              .toLowerCase()
                              .includes(filters.search!.toLowerCase()) ||
                          o.user.name
                              .toLowerCase()
                              .includes(filters.search!.toLowerCase())
                        : true,
                )
                .filter(o =>
                    o.products
                        .filter(p =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter?.includes(p.name)
                                : true,
                        )
                        .filter(p =>
                            filters.search
                                ? (p.meta.teamName
                                      ? p.meta.teamName
                                            .toLowerCase()
                                            .includes(
                                                filters.search.toLowerCase(),
                                            )
                                      : false) ||
                                  p.description
                                      .toLowerCase()
                                      .includes(
                                          filters.search!.toLowerCase(),
                                      ) ||
                                  o.user.email
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase()) ||
                                  o.user.name
                                      .toLowerCase()
                                      .includes(filters.search.toLowerCase())
                                : true,
                        ),
                )
                .sort((a, b) => {
                    return (
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                }),
        [orders, filters],
    );

    const totals = useMemo(() => {
        if (filteredOrders.length > 0) {
            const total = filteredOrders.reduce(
                (acc, o) => acc + (o.total - o.discount),
                0,
            );
            const unpaid = filteredOrders
                .filter(o => o.status !== "completed")
                .reduce((acc, o) => acc + (o.total - o.discount), 0);
            const count = filteredOrders.reduce(
                (acc, o) =>
                    acc +
                    o.products.filter(p => p.status !== "cancelled").length,
                0,
            );

            return { total, unpaid, count };
        } else {
            return {
                total: 0,
                unpaid: 0,
                count: 0,
            };
        }
    }, [filteredOrders, filters.orderDateStart, filters.orderDateEnd]);

    const contextValue = useMemo(
        () => ({
            fetchStatus,
            setFetchStatus,
            filters,
            setFilters,
            orders: filteredOrders,
            setOrders,
            totals,
            tournaments,
            masterTournaments,
            defaultStatusFilters,
        }),
        [fetchStatus, filteredOrders],
    );

    return (
        <TournamentRegistrationsContext.Provider value={contextValue}>
            {children}
        </TournamentRegistrationsContext.Provider>
    );
};
